/**
 * Single source of truth for the subtotal/service-fee/parafiscal-tax/total math shared by the
 * public cart, POS sales, and any other place that needs to price a purchasable line.
 * Never use this to derive values sent to the payment gateway or third-party integrations —
 * those keep their own untouched, backend-resolved numeric contracts.
 */
export function roundCurrency(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

export interface LineBreakdownInput {
  base: number
  serviceFeeAmount?: number | undefined
  serviceFeePercent?: number | undefined
  parafiscalTax?: number | undefined
  totalAmount?: number | undefined
}

export interface LineBreakdown {
  base: number
  serviceFee: number
  parafiscalTax: number
  total: number
}

export function computeLineBreakdown(input: LineBreakdownInput): LineBreakdown {
  const serviceFee = input.serviceFeeAmount
    ?? roundCurrency(input.base * (input.serviceFeePercent ?? 0) / 100)
  const parafiscalTax = input.parafiscalTax ?? 0
  const total = input.totalAmount ?? input.base + serviceFee + parafiscalTax
  const base = input.totalAmount !== undefined ? total - serviceFee - parafiscalTax : input.base
  return { base, serviceFee, parafiscalTax, total }
}

export interface LineBreakdownWithQuantity {
  breakdown: LineBreakdown
  quantity: number
}

export interface AggregatedBreakdown {
  subtotal: number
  serviceFee: number
  parafiscalTax: number
  total: number
}

export function sumLineBreakdowns(lines: LineBreakdownWithQuantity[]): AggregatedBreakdown {
  return lines.reduce<AggregatedBreakdown>((totals, line) => ({
    subtotal: totals.subtotal + line.breakdown.base * line.quantity,
    serviceFee: totals.serviceFee + line.breakdown.serviceFee * line.quantity,
    parafiscalTax: totals.parafiscalTax + line.breakdown.parafiscalTax * line.quantity,
    total: totals.total + line.breakdown.total * line.quantity
  }), { subtotal: 0, serviceFee: 0, parafiscalTax: 0, total: 0 })
}
